import {
  UICorePlugin,
  template,
  type EventMap,
  type TemplateStringifier,
  type SupportedVersion
} from '@clappr/core'
import {Events} from 'common/types/events'
import type {Options} from 'common/types/options'
import type {MediaControl} from 'common/plugins/media-control'
import type {Advertisement} from 'common/plugins/advertisement'
import pluginHtml from './template.html'

interface Rate {
  value: string
  buttonValue: string
  label: string
  className: string
  wrapperClassName: string
}

const DEFAULT_PLAYBACK_RATE = '1.0'

export class PlaybackRate<T extends Options> extends UICorePlugin<T> {
  private static PLAYBACK_RATES = [
    {
      value: '0.25',
      buttonValue: '0.50',
      label: '0.25',
      className: 'icon-fast-forward icon-faster-x025',
      wrapperClassName: 'eplayer-speed-x025-button'
    },
    {
      value: '0.50',
      buttonValue: '0.75',
      label: '0.5',
      className: 'icon-fast-forward icon-faster-x050',
      wrapperClassName: 'eplayer-speed-x050-button'
    },
    {
      value: '0.75',
      buttonValue: '1',
      label: '0.75',
      className: 'icon-fast-forward icon-faster-x075',
      wrapperClassName: 'eplayer-speed-x075-button'
    },
    {
      value: '1.0',
      buttonValue: '1.25',
      label: '1',
      className: 'icon-fast-forward icon-faster-x1',
      wrapperClassName: 'eplayer-speed-x1-button'
    },
    {
      value: '1.25',
      buttonValue: '1.5',
      label: '1.25',
      className: 'icon-faster-x125',
      wrapperClassName: 'eplayer-speed-x125-button'
    },
    {
      value: '1.5',
      buttonValue: '1.75',
      label: '1.5',
      className: 'icon-faster-x15',
      wrapperClassName: 'eplayer-speed-x15-button'
    },
    {
      value: '1.75',
      buttonValue: '2.0',
      label: '1.75',
      className: 'icon-faster-x175',
      wrapperClassName: 'eplayer-speed-x175-button'
    },
    {
      value: '2.0',
      buttonValue: '2.25',
      label: '2',
      className: 'icon-faster-x2',
      wrapperClassName: 'eplayer-speed-x2-button'
    }
  ]

  private static selectedRate = DEFAULT_PLAYBACK_RATE

  private playbackRates!: Rate[]
  private $wrapper!: ZeptoCollection
  private $container?: ZeptoCollection

  public get name(): string {
    return 'playbackRate'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get template(): TemplateStringifier {
    return template(pluginHtml)
  }

  public get events(): EventMap {
    return {
      'click .icon': 'onRateSelect'
    }
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  private get advertisement(): Advertisement<T> | null {
    return this.core.getPlugin('advertisement') as Advertisement<T> | null
  }

  public get selectedRate(): string {
    return PlaybackRate.selectedRate
  }

  public get playbackRatesList(): Rate[] {
    return this.playbackRates
  }

  public bindEvents(): void {
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.resetEvents
    )

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_PLAYBACKRATE_CHANGED,
        this.updatePlaybackRate
      )
    }

    if (this.advertisement) {
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_STARTED,
        this.onAdStarted
      )
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_CONTENT_RESUME_REQUESTED,
        this.onAdContentResumeRequested
      )
    }
  }

  public unBindEvents(): void {
    this.stopListening(this.core, Events.CORE_ACTIVE_CONTAINER_CHANGED)

    if (this.mediaControl) {
      this.stopListening(this.mediaControl, Events.MEDIACONTROL_RENDERED)
    }
  }

  public resetEvents(): void {
    this.unBindEvents()
    this.bindEvents()
    this.onActiveContainerChange()
  }

  private onActiveContainerChange(): void {
    this.setSelectedRate(this.selectedRate)
  }

  public shouldRender(): boolean {
    if (!this.core.activeContainer || !this.$container?.length) {
      return false
    }

    const {tagName} = this.core.activePlayback

    return tagName === 'video' || tagName === 'audio'
  }

  protected onAdStarted(): void {
    this.setSelectedRate(DEFAULT_PLAYBACK_RATE, false)
  }

  protected onAdContentResumeRequested(): void {
    this.setSelectedRate(this.selectedRate)
  }

  public onRateSelect(event: MouseEvent): boolean {
    const {playbackRateSelect = ''} = (event.target as HTMLElement).dataset

    this.setSelectedRate(playbackRateSelect)
    event.stopPropagation()

    return false
  }

  public updatePlaybackRate(rate: string): void {
    this.setActiveClassName(rate)
  }

  public setSelectedRate(rate: string, updateState = true): void {
    const mediaElement = this.core.$el.find('video, audio').get(0)

    if (!mediaElement) {
      return
    }

    // Set <video playbackRate="..."
    ;(mediaElement as HTMLVideoElement).playbackRate = parseFloat(rate)

    if (updateState) {
      PlaybackRate.selectedRate = rate

      if (this.$container?.length) {
        this.setActiveClassName(rate)
      }
    }
  }

  public getRateDiff(value: string): Rate[] {
    return this.playbackRates.reduce(
      // eslint-disable-next-line max-params
      (acc: Rate[], item: Rate, index, array) => {
        if (acc.length > 0) {
          return acc
        }

        if (item.value === value) {
          return [index > 0 ? array[index - 1] : array[array.length - 1], item]
        }

        return []
      },
      []
    )
  }

  public setActiveClassName(rateValue: string): void {
    const [previousRate, currentRate] = this.getRateDiff(rateValue)

    if (!currentRate) {
      return
    }

    this.$wrapper.addClass(currentRate.wrapperClassName)
    this.$wrapper.removeClass(previousRate.wrapperClassName)
  }

  public render(): this {
    if (!this.playbackRates) {
      this.playbackRates = PlaybackRate.PLAYBACK_RATES
    }

    this.$container = this.mediaControl?.$el.find('.eplayer-speed')

    if (this.shouldRender() && this.$container) {
      this.setElement(this.$container)
      this.$el.html(
        this.template({
          playbackRates: this.playbackRates
        })
      )
      this.$wrapper = this.$el.find('.eplayer-faster')
      this.setActiveClassName(this.selectedRate)
      this.$el.css('display', 'block')
    }

    return this
  }
}
